import type { LockedProperty, MapPoint } from "../types";

// pinStore — the last slim map payload (`?fields=map`) per user + county,
// persisted in IndexedDB so a page reload draws that county's pins at once
// instead of waiting on the network (load-time work, 2026-10-22). The slim
// and full requests still run on every visit and replace these within a
// second or two; a persisted entry only ever fills the gap before they land.
//
// WHY THIS IS ALLOWED TO PERSIST when propertiesCache is memory-only: a slim
// payload carries no plan-gated identity data. A MapPoint is id, county,
// status, ZIP, coordinates and an upset-window date; a locked teaser is city,
// state, ZIP, a value bucket and coordinates. Owner, mailing address, value
// and every other gated field live only in the FULL response, which is never
// written here. So the shared-computer concern behind propertiesCache's rule
// (one user's unlocked owner data left on disk) does not apply. What can be
// stale is which pins are locked, for the moment before the fresh slim
// response replaces it, which is why entries also expire after a day.
//
// Keyed by propertiesCacheKey (user id + county). clearPersistedPins() runs
// on sign-out and from clearPropertiesCache(), and carries the same
// generation guard: a request that started before a clear cannot write its
// response back afterwards, and a read that straddles a clear is dropped.
//
// Best-effort throughout: no IndexedDB (private windows, old browsers, jsdom),
// a blocked open, a quota error — every failure just means no instant pins,
// exactly the behavior before this store existed.

export interface PersistedPins {
  points: MapPoint[];
  lockedProperties: LockedProperty[];
  savedAt: number;
}

export interface PinRecord extends PersistedPins {
  key: string;
}

/** Storage seam: IndexedDB in the browser, an in-memory fake in tests. */
export interface PinBackend {
  get(key: string): Promise<PinRecord | undefined>;
  /** Stores record, then drops the oldest entries beyond maxEntries. */
  put(record: PinRecord, maxEntries: number): Promise<void>;
  clear(): Promise<void>;
}

export const PIN_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const PIN_MAX_ENTRIES = 8;

const DB_NAME = "parcely-map-pins";
const STORE = "pins";

function request<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const open = indexedDB.open(DB_NAME, 1);
      open.onupgradeneeded = () => {
        const store = open.result.createObjectStore(STORE, { keyPath: "key" });
        store.createIndex("savedAt", "savedAt");
      };
      open.onsuccess = () => {
        const db = open.result;
        // Another tab upgrading the schema: let it, and reopen next time.
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      open.onerror = () => reject(open.error);
      open.onblocked = () => reject(new Error("pin store open blocked"));
    }).catch((err) => {
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

const indexedDbBackend: PinBackend = {
  async get(key) {
    const db = await openDb();
    return request<PinRecord | undefined>(db.transaction(STORE).objectStore(STORE).get(key));
  },
  async put(record, maxEntries) {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    store.put(record);
    // Primary keys in savedAt order, oldest first; includes the put above,
    // since requests in one transaction run in order.
    const keys = await request(store.index("savedAt").getAllKeys());
    for (const k of keys.slice(0, Math.max(0, keys.length - maxEntries))) {
      store.delete(k);
    }
    await done(tx);
  },
  async clear() {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    await done(tx);
  },
};

let backend: PinBackend | null = typeof indexedDB !== "undefined" ? indexedDbBackend : null;
let generation = 0;

/** Test seam: swap the storage (null = no persistence). */
export function setPinBackendForTest(b: PinBackend | null): void {
  backend = b;
}

/** The current generation. Capture it when a slim request starts. */
export function pinStoreGeneration(): number {
  return generation;
}

/**
 * Returns the persisted pins for key, or null when there are none, they are
 * older than PIN_MAX_AGE_MS, storage is unavailable, or the store was cleared
 * while the read was in flight.
 */
export async function readPersistedPins(key: string): Promise<PersistedPins | null> {
  if (!backend) return null;
  const startedAt = generation;
  try {
    const rec = await backend.get(key);
    if (!rec || startedAt !== generation) return null;
    if (Date.now() - rec.savedAt > PIN_MAX_AGE_MS) return null;
    return { points: rec.points, lockedProperties: rec.lockedProperties, savedAt: rec.savedAt };
  } catch {
    return null;
  }
}

/**
 * Persists a slim response for key, unless the store was cleared after the
 * request started (startedAtGeneration is stale). Fire-and-forget.
 */
export function persistPins(
  key: string,
  pins: { points: MapPoint[]; lockedProperties: LockedProperty[] },
  startedAtGeneration: number,
): void {
  if (!backend || startedAtGeneration !== generation) return;
  backend
    .put({ key, points: pins.points, lockedProperties: pins.lockedProperties, savedAt: Date.now() }, PIN_MAX_ENTRIES)
    .catch(() => {});
}

/** Drops every persisted entry and invalidates reads/writes in flight. */
export function clearPersistedPins(): void {
  generation++;
  backend?.clear().catch(() => {});
}
